import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import { getAllLocations } from 'features/Location/services/location'
import { fetchMasterAreaPickingListAPI } from 'features/MasterAreaPicking/services/masterAreaPicking'
import { listRouteWaves } from 'features/Midmile/service/routeAndSchedule'
import {
  getPrintSupplyOrderGroupAPI,
  GetPrintSupplyOrderGroupResponseType,
  GetSOPickingCompilationResponseType,
  getStaffManagementAPI,
  getSupplyOrderDeliveryDocumentAPI,
  getSupplyOrderDeliveryDocumentLoadingAPI,
  getSupplyOrderDeliveryDocumentLoadingDetailAPI,
  getSupplyOrderGrandDataAPI,
  GetSupplyOrderGrandDataResponseType,
  getSupplyOrderGrandKoliLabelAPI,
  getSupplyOrderListAPI,
  getSupplyOrderPackingDetailAPI,
  getSupplyOrderRemarksAPI,
  postNewGroupSupplyOrderAPI,
  postSOPickingCompilationAPI,
  postSupplyOrderAssignPickerAPI,
  postSupplyOrderBulkAssignPickerAPI,
  postSupplyOrderCopyAPI,
  putCancelGroupSupplyOrderAPI,
  putStartSoLoadingActivityAPI,
  putSupplyOrderStatusAPI,
} from 'features/SupplyOrder/services/outboundSO'
import { getFeatureFlag } from 'features/Location/services/featureFlag'
import { callErrorMsg } from 'helpers/errorMsg'
import { getFeatureFlagByLocationID } from 'features/SupplyOrder/services/packingRecording'

export const SLICE_NAME = 'OUTBOUND_SO'

export const fetchGetSupplyOrder = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrder`,
  async (params: Parameters<typeof getSupplyOrderListAPI>[0], { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderListAPI(params)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetFeatureFlags = createAsyncThunk(
  `${SLICE_NAME}/fetchGetFeatureFlags`,
  async (
    props: {
      locationId: number
      featureFlag: string
    },
    { rejectWithValue },
  ) => {
    try {
      const { locationId, featureFlag } = props

      const response = await getFeatureFlag(locationId, featureFlag)

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetFeatureFlagByLocationID = createAsyncThunk(
  `${SLICE_NAME}/fetchGetFeatureFlagByLocationID`,
  async (locationId: number, { rejectWithValue }) => {
    try {
      const response = await getFeatureFlagByLocationID(locationId)

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderRemarks = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderRemarks`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderRemarksAPI()

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetAllLocation = createAsyncThunk(
  `${SLICE_NAME}/fetchGetAllLocation`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllLocations()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetRouteMaster = createAsyncThunk(
  `${SLICE_NAME}/fetchGetRouteMaster`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await listRouteWaves({ page_size: 1000, active: true })

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetPickingArea = createAsyncThunk(
  `${SLICE_NAME}/fetchGetPickingArea`,
  async (locationId: number, { rejectWithValue }) => {
    try {
      const response = await fetchMasterAreaPickingListAPI({
        page_size: '9999',
        location_id: locationId,
      })

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostCopySupplyOrder = createAsyncThunk(
  `${SLICE_NAME}/fetchPostCopySupplyOrder`,
  async (
    props: {
      supplyOrderId: number
      onSuccess?: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, onSuccess } = props

      const response = await postSupplyOrderCopyAPI(supplyOrderId)

      onSuccess?.()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutSupplyOrderStatus = createAsyncThunk(
  `${SLICE_NAME}/fetchPutSupplyOrderStatus`,
  async (
    props: {
      supplyOrderId: number
      payload: { status: string }
      onSuccess?: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, payload, onSuccess } = props

      const response = await putSupplyOrderStatusAPI(supplyOrderId, payload)

      onSuccess?.()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetPicker = createAsyncThunk(
  `${SLICE_NAME}/fetchGetPicker`,
  async (locationId: number, { rejectWithValue }) => {
    try {
      const res = await getStaffManagementAPI({
        locationId,
        role: 'AUTH_OUTBOUND_PICKER',
        params: {
          locationType: 'WAREHOUSE',
        },
      })

      return res.data.data
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderGrandData = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderGrandData`,
  async (
    props: {
      supplyOrderId: number
      params?: {
        staffIdList: number
      }
      onSuccess?: (data: GetSupplyOrderGrandDataResponseType['data']) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, params, onSuccess } = props

      const response = await getSupplyOrderGrandDataAPI(supplyOrderId, params)

      onSuccess?.(response.data.data)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostAssignPicker = createAsyncThunk(
  `${SLICE_NAME}/fetchPostAssignPicker`,
  async (
    props: {
      supplyOrderId: number
      payload: {
        pickerId: number
      }
      onSuccess?: (message: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, payload, onSuccess } = props

      const response = await postSupplyOrderAssignPickerAPI(supplyOrderId, payload)

      onSuccess?.(response.data.data?.message || 'Success')

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostBulkAssignPicker = createAsyncThunk(
  `${SLICE_NAME}/fetchPostBulkAssignPicker`,
  async (
    props: {
      payload: Parameters<typeof postSupplyOrderBulkAssignPickerAPI>[0]
      onSuccess?: (message: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { payload, onSuccess } = props

      const response = await postSupplyOrderBulkAssignPickerAPI(payload)

      onSuccess?.(response.data?.message || 'Success')

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderPackingDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderPackingDetail`,
  async (supplyOrderId: number, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderPackingDetailAPI(supplyOrderId)

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderGrandKoliLabel = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderGrandKoliLabel`,
  async (supplyOrderId: number, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderGrandKoliLabelAPI(supplyOrderId)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderDeliveryDocument = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderDeliveryDocument`,
  async (supplyOrderIds: string, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderDeliveryDocumentAPI({ id: supplyOrderIds })

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderDeliveryDocumentLoading = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderDeliveryDocumentLoading`,
  async (loadingIDs: string, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderDeliveryDocumentLoadingAPI({ loadingIDs })

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderDeliveryDocumentLoadingDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderDeliveryDocumentLoadingDetail`,
  async (loadingId: number, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderDeliveryDocumentLoadingDetailAPI(loadingId)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPostCreateGroupSO = createAsyncThunk(
  `${SLICE_NAME}/fetchPostCreateGroupSO`,
  async (
    props: {
      payload: {
        supplyOrderIds: number[]
      }
      onSuccess?: (message: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { payload, onSuccess } = props

      const response = await postNewGroupSupplyOrderAPI(payload)

      onSuccess?.(response.data?.message)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutCancelGroupSO = createAsyncThunk(
  `${SLICE_NAME}/fetchPutCancelGroupSO`,
  async (
    props: {
      groupId: string
      onSuccess?: (message: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { groupId, onSuccess } = props
      const response = await putCancelGroupSupplyOrderAPI(groupId)

      onSuccess?.(response.data?.message)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutStartSupplyOrderLoading = createAsyncThunk(
  `${SLICE_NAME}/fetchPutStartSupplyOrderLoading`,
  async (
    props: {
      loadingId: number
      onSuccess?: (message: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { loadingId, onSuccess } = props
      const res = await putStartSoLoadingActivityAPI({
        loading_id: loadingId,
      })

      onSuccess?.(res.data?.message || 'Berhasil memulai loading.')

      return res.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetPrintSupplyOrderGroup = createAsyncThunk(
  `${SLICE_NAME}/fetchGetPrintSupplyOrderGroup`,
  async (
    props: {
      groupId: string
      onSuccess?: (data: GetPrintSupplyOrderGroupResponseType) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { groupId, onSuccess } = props
      const response = await getPrintSupplyOrderGroupAPI(groupId)

      onSuccess?.(response.data)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSOPickingCompilation = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSOPickingCompilation`,
  async (
    props: {
      supplyOrderId: number
      onSuccess?: (data: GetSOPickingCompilationResponseType) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, onSuccess } = props
      const response = await postSOPickingCompilationAPI(supplyOrderId)

      onSuccess?.(response.data)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
